import { useEffect, useState } from "react";
import NavBar from "../../../components/organisms/navbar";
import { FaEdit, FaPlus, FaEye, FaEyeSlash } from "react-icons/fa";
import {
  AthletesLink,
  Container,
  Content,
  HeaderRow,
  PageLocationTitle,
  SectionTitle,
  PageTitle,
  AthleteCard,
  AthleteAvatar,
  AthleteInfo,
  AthleteNameRow,
  AthleteName,
  AthleteAge,
  AthleteMetaRow,
  BeltBadge,
  MainAthleteTag,
  CardActions,
  EyeButton,
  EditButton,
  ExpandedDetails,
  AthleteDetail,
  AthleteDetailLabel,
} from "./styles";
import api from "../../../services/api";
import { toast } from "react-toastify";
import useAuthenticate from "../../../hooks/useAuthenticate";
import Loading from "../../../components/atoms/loading";

const belts = [
  { title: "Branca", option: 1 },
  { title: "Cinza", option: 2 },
  { title: "Amarela", option: 3 },
  { title: "Laranja", option: 4 },
  { title: "Verde", option: 5 },
  { title: "Azul", option: 6 },
  { title: "Roxa", option: 7 },
  { title: "Marrom", option: 8 },
  { title: "Preta", option: 9 },
];

const getBeltName = (beltId: number) => belts.find((b) => b.option === beltId)?.title || "";

const getAvatarUrl = (avatar?: string | null) => {
  if (!avatar) return undefined;
  return `${api.defaults.baseURL?.replace(/\/$/, "") || ""}${avatar}`;
};

const calcAge = (birthDate?: string) => {
  if (!birthDate) return "";
  const parts = birthDate.split("/");
  if (parts.length !== 3) return "";
  const birth = new Date(+parts[2], +parts[1] - 1, +parts[0]);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return `${age} anos`;
};

const Athletes = () => {
  const { account } = useAuthenticate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { getToken } = useAuthenticate();

  const toggle = (id: string) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  useEffect(() => {
    setLoading(true);
    if (!account) return;
    api
      .get("/fighters", { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(({ data }) => { setLoading(false); setData(data); })
      .catch(() => { setLoading(false); toast.error("Não foi possível carregar os atletas."); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loading text="Aguarde, estamos processando os dados." />;

  return (
    <>
      <NavBar />
      <Container>
        <Content>
          {/* Header */}
          <HeaderRow>
            <PageLocationTitle>Meus Atletas</PageLocationTitle>
            <AthletesLink to="/athletes">
              <FaPlus size={11} /> Novo Atleta
            </AthletesLink>
          </HeaderRow>

          {/* Atleta Principal */}
          <SectionTitle>Atleta Principal</SectionTitle>

          <AthleteCard>
            <AthleteAvatar $src={getAvatarUrl(account?.avatar)}>
              {!account?.avatar && (account?.name?.charAt(0)?.toUpperCase() || "👤")}
            </AthleteAvatar>
            <AthleteInfo>
              <AthleteNameRow>
                <AthleteName>{account?.name?.toUpperCase()}</AthleteName>
                <AthleteAge>{calcAge(account?.birth_date)}</AthleteAge>
                <MainAthleteTag>Principal</MainAthleteTag>
              </AthleteNameRow>
              <AthleteMetaRow>
                <BeltBadge $belt={account?.belt}>{getBeltName(account?.belt || 0)}</BeltBadge>
              </AthleteMetaRow>
              {expanded["main"] && (
                <ExpandedDetails>
                  <AthleteDetail><AthleteDetailLabel>Email:</AthleteDetailLabel> {account?.email}</AthleteDetail>
                  <AthleteDetail><AthleteDetailLabel>Mãe:</AthleteDetailLabel> {account?.mother_name?.toUpperCase()}</AthleteDetail>
                  <AthleteDetail><AthleteDetailLabel>Nascimento:</AthleteDetailLabel> {account?.birth_date}</AthleteDetail>
                  <AthleteDetail><AthleteDetailLabel>Telefone:</AthleteDetailLabel> {account?.phone}</AthleteDetail>
                  <AthleteDetail><AthleteDetailLabel>Sexo:</AthleteDetailLabel> {account?.genre?.toUpperCase()}</AthleteDetail>
                </ExpandedDetails>
              )}
            </AthleteInfo>
            <CardActions>
              <EyeButton onClick={() => toggle("main")} title={expanded["main"] ? "Esconder" : "Ver detalhes"}>
                {expanded["main"] ? <FaEyeSlash /> : <FaEye />}
              </EyeButton>
              <EditButton href="/profile/edit/user" title="Editar">
                <FaEdit />
              </EditButton>
            </CardActions>
          </AthleteCard>

          {/* Atletas Vinculados */}
          <SectionTitle>
            Atletas Vinculados
            <PageTitle> &mdash; <span translate="no">{data.length}</span> encontrado{data.length !== 1 ? "s" : ""}</PageTitle>
          </SectionTitle>

          {data.map((v: any) => (
            <AthleteCard key={v.id}>
              <AthleteAvatar $src={getAvatarUrl(v.avatar)}>
                {!v.avatar && (v.name?.charAt(0)?.toUpperCase() || "👤")}
              </AthleteAvatar>
              <AthleteInfo>
                <AthleteNameRow>
                  <AthleteName>{v.name.toUpperCase()}</AthleteName>
                  <AthleteAge>{calcAge(v.birth_date)}</AthleteAge>
                </AthleteNameRow>
                <AthleteMetaRow>
                  <BeltBadge $belt={v.belt}>{getBeltName(v.belt)}</BeltBadge>
                </AthleteMetaRow>
                {expanded[v.id] && (
                  <ExpandedDetails>
                    <AthleteDetail><AthleteDetailLabel>Mãe:</AthleteDetailLabel> {v.mother_name.toUpperCase()}</AthleteDetail>
                    <AthleteDetail><AthleteDetailLabel>Nascimento:</AthleteDetailLabel> {v.birth_date}</AthleteDetail>
                    <AthleteDetail><AthleteDetailLabel>Telefone:</AthleteDetailLabel> {v.phone}</AthleteDetail>
                    <AthleteDetail><AthleteDetailLabel>Sexo:</AthleteDetailLabel> {v.genre.toUpperCase()}</AthleteDetail>
                  </ExpandedDetails>
                )}
              </AthleteInfo>
              <CardActions>
                <EyeButton onClick={() => toggle(v.id)} title={expanded[v.id] ? "Esconder" : "Ver detalhes"}>
                  {expanded[v.id] ? <FaEyeSlash /> : <FaEye />}
                </EyeButton>
                <EditButton href={`/profile/athletes/${v.id}`} title="Editar">
                  <FaEdit />
                </EditButton>
              </CardActions>
            </AthleteCard>
          ))}
        </Content>
      </Container>
    </>
  );
};

export default Athletes;
